<template>
  <v-card>
    <v-card-title>
      {{ $t("plan.template-management") }}
    </v-card-title>
    <v-card-text>
      <v-row class="mb-3">
        <v-col>
          <v-btn color="primary" @click="showAddDialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("plan.add-template") }}
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="templateHeaders"
        :items="templates"
        class="elevation-1"
      >
        <template v-slot:item.type="{ item }">
          <v-chip :color="item.is_default ? 'blue' : 'green'" small dark>
            {{
              item.is_default
                ? $t("plan.default-template")
                : $t("plan.custom-template")
            }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row>
            <v-col>
              <v-file-input
                v-model="uploadFiles[item.value]"
                :label="$t('plan.upload-new')"
                accept=".docx"
                hide-details
                dense
                outlined
                prepend-icon=""
                @change="prepareUpload(item.value)"
              ></v-file-input>
            </v-col>
            <v-col class="d-flex align-center">
              <v-btn
                color="primary"
                small
                :disabled="!uploadFiles[item.value]"
                @click="uploadTemplate(item.value)"
              >
                <v-icon left>mdi-upload</v-icon>
                {{ $t("plan.upload") }}
              </v-btn>
              <v-btn
                color="error"
                small
                class="ml-2"
                @click="resetTemplate(item.value)"
              >
                <v-icon left>mdi-refresh</v-icon>
                {{ $t("plan.reset") }}
              </v-btn>
              <v-btn
                color="success"
                small
                class="ml-2"
                @click="downloadTemplate(item.value)"
              >
                <v-icon left>mdi-download</v-icon>
                {{ $t("plan.download-custom-template") }}
              </v-btn>
              <v-btn
                color="warning"
                small
                class="ml-2"
                :disabled="item.is_default"
                @click="confirmRemoveTemplate(item)"
              >
                <v-icon left>mdi-delete</v-icon>
                {{ $t("plan.remove-template") }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Add Template Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t("plan.add-template") }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newTemplateName"
            :label="$t('plan.template-name-input')"
            outlined
            dense
            required
          ></v-text-field>
          <v-file-input
            v-model="newTemplateFile"
            :label="$t('plan.upload-new')"
            accept=".docx"
            outlined
            dense
            required
          ></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showAddDialog = false">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn color="blue darken-1" text @click="addTemplate">{{
            $t("common.confirm")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Template Confirmation Dialog -->
    <v-dialog v-model="showRemoveDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t("plan.remove-template") }}</v-card-title>
        <v-card-text>{{ $t("plan.template-remove-confirm") }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showRemoveDialog = false">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn color="red darken-1" text @click="removeTemplate">{{
            $t("common.confirm")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Message Dialog -->
    <v-dialog v-model="dialog_message" max-width="500px">
      <v-card>
        <v-card-title>{{ message.title }}</v-card-title>
        <v-card-text>{{ message.text }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialog_message = false">{{
            $t("common.confirm")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-card>
</template>

<script>
export default {
  name: "TemplateManagement",
  data: () => ({
    search: "",
    dialog_message: false,
    message: {
      title: "",
      text: "",
    },
    loading: false,
    templates: [],
    uploadFiles: {},
    templateHeaders: [
      { text: "plan.template-name", value: "text" },
      { text: "plan.template-type", value: "type", sortable: false },
      { text: "plan.actions", value: "actions", sortable: false },
    ],
    showAddDialog: false,
    newTemplateName: "",
    newTemplateFile: null,
    showRemoveDialog: false,
    templateToRemove: null,
  }),
  computed: {
    token() {
      return this.$store.state.token;
    },
  },
  created() {
    // Translate headers
    this.templateHeaders = this.templateHeaders.map((header) => {
      return {
        ...header,
        text: this.$t(header.text),
      };
    });
    // Fetch templates from backend
    this.fetchTemplates();
  },
  methods: {
    fetchTemplates() {
      const url = `${process.env.VUE_APP_SERVER_URL}/plan/transcribe/list_templates/`;

      this.loading = true;

      this.axios
        .get(url)
        .then((response) => {
          this.templates = response.data.templates || [];
        })
        .catch((error) => {
          console.error("Error fetching templates:", error);
          this.message = {
            title: this.$t("common.warning"),
            text: this.$t("plan.template-add-error"),
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    addTemplate() {
      if (!this.newTemplateName || !this.newTemplateFile) {
        this.message = {
          title: this.$t("common.warning"),
          text: this.$t("plan.template-required-fields"),
        };
        this.dialog_message = true;
        return;
      }

      const formData = new FormData();
      formData.append("template_file", this.newTemplateFile);
      formData.append("template_name", this.newTemplateName);

      const url = `${process.env.VUE_APP_SERVER_URL}/plan/transcribe/add_template/`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Token " + this.token,
        },
      };

      this.loading = true;

      this.axios
        .post(url, formData, config)
        .then((response) => {
          this.message = {
            title: this.$t("common.warning"),
            text: this.$t("plan.template-add-success"),
          };
          this.dialog_message = true;
          this.showAddDialog = false;
          this.newTemplateName = "";
          this.newTemplateFile = null;
          // Refresh template list
          this.fetchTemplates();
        })
        .catch((error) => {
          let errorMessage = this.$t("plan.template-add-error");
          if (
            error.response &&
            error.response.data &&
            error.response.data.error_message
          ) {
            errorMessage = error.response.data.error_message;
          }

          this.message = {
            title: this.$t("common.warning"),
            text: errorMessage,
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    confirmRemoveTemplate(item) {
      if (item.is_default) {
        this.message = {
          title: this.$t("common.warning"),
          text: this.$t("plan.cannot-remove-default"),
        };
        this.dialog_message = true;
        return;
      }
      this.templateToRemove = item;
      this.showRemoveDialog = true;
    },

    removeTemplate() {
      if (!this.templateToRemove) {
        return;
      }

      const url = `${process.env.VUE_APP_SERVER_URL}/plan/transcribe/remove_template/`;
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + this.token,
        },
      };

      this.loading = true;

      this.axios
        .post(url, { template_name: this.templateToRemove.value }, config)
        .then((response) => {
          this.message = {
            title: this.$t("common.warning"),
            text: this.$t("plan.template-remove-success"),
          };
          this.dialog_message = true;
          this.showRemoveDialog = false;
          this.templateToRemove = null;
          // Refresh template list
          this.fetchTemplates();
        })
        .catch((error) => {
          let errorMessage = this.$t("plan.template-remove-error");
          if (
            error.response &&
            error.response.data &&
            error.response.data.error_message
          ) {
            errorMessage = error.response.data.error_message;
          }

          this.message = {
            title: this.$t("common.warning"),
            text: errorMessage,
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    prepareUpload(templateName) {
      // This method is called when a file is selected for a specific template
      // No need to do anything here, the v-model binding will update uploadFiles[templateName]
    },

    uploadTemplate(templateName) {
      if (!this.uploadFiles[templateName]) {
        this.message = {
          title: this.$t("common.warning"),
          text: this.$t("plan.template-required-fields"),
        };
        this.dialog_message = true;
        return;
      }

      const formData = new FormData();
      formData.append("template_file", this.uploadFiles[templateName]);
      formData.append("template_name", templateName);

      const url = `${process.env.VUE_APP_SERVER_URL}/plan/transcribe/upload_templates/`;
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Token " + this.token,
        },
      };

      this.loading = true;

      this.axios
        .post(url, formData, config)
        .then((response) => {
          this.message = {
            title: this.$t("common.warning"),
            text: this.$t("plan.template-upload-success"),
          };
          this.dialog_message = true;
          // Clear the file input
          this.$set(this.uploadFiles, templateName, null);
        })
        .catch((error) => {
          let errorMessage = this.$t("plan.template-upload-error");
          if (
            error.response &&
            error.response.data &&
            error.response.data.error_message
          ) {
            errorMessage = error.response.data.error_message;
          }

          this.message = {
            title: this.$t("common.warning"),
            text: errorMessage,
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    resetTemplate(templateName) {
      const url = `${process.env.VUE_APP_SERVER_URL}/plan/transcribe/reset_templates/`;
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + this.token,
        },
      };

      this.loading = true;

      this.axios
        .post(url, { template_name: templateName }, config)
        .then((response) => {
          this.message = {
            title: this.$t("common.warning"),
            text: this.$t("plan.template-reset-success"),
          };
          this.dialog_message = true;
        })
        .catch((error) => {
          let errorMessage = this.$t("plan.template-reset-error");
          if (
            error.response &&
            error.response.data &&
            error.response.data.error_message
          ) {
            errorMessage = error.response.data.error_message;
          }

          this.message = {
            title: this.$t("common.warning"),
            text: errorMessage,
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    downloadTemplate(templateName) {
      // Create the download URL with the template name as a query parameter
      const url = `${
        process.env.VUE_APP_SERVER_URL
      }/plan/transcribe/download_template/?template_name=${encodeURIComponent(
        templateName
      )}`;

      // Set the authorization header
      const config = {
        headers: {
          Authorization: "Token " + this.token,
        },
        responseType: "blob", // Important for downloading files
      };

      this.loading = true;

      this.axios
        .get(url, config)
        .then((response) => {
          // Create a blob from the response data
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          // Get filename from Content-Disposition header if available
          let filename = `${templateName}.docx`;
          const contentDisposition = response.headers["content-disposition"];
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/i);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1];
            }
          }

          // Create a link element and trigger the download
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();

          // Clean up
          window.URL.revokeObjectURL(link.href);
        })
        .catch((error) => {
          let errorMessage = this.$t("plan.template-download-error");
          if (error.response) {
            if (error.response.status === 404) {
              errorMessage = this.$t("plan.no-custom-template");
            } else if (
              error.response.data &&
              error.response.data.error_message
            ) {
              errorMessage = error.response.data.error_message;
            }
          }

          this.message = {
            title: this.$t("common.warning"),
            text: errorMessage,
          };
          this.dialog_message = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
